import {entries, mapValues, partition, take} from 'remeda'
import {match, P} from 'ts-pattern'
import {z, ZodObject, ZodRawShape, ZodTypeAny, type ZodSchema} from 'zod'
import {DateTime} from 'luxon'
import {err, ok, type Err, type OK, type Res} from './promise'

type UnionToIntersection<U> = (U extends never ? never : (arg: U) => never) extends (arg: infer I) => void ? I : never

export type UnionToTuple<T> =
  UnionToIntersection<T extends never ? never : (t: T) => T> extends (_: never) => infer W ?
    [...UnionToTuple<Exclude<T, W>>, W]
  : []

export type PlainObject = {[key: string]: Plain}
export type PlainObjectFlat = {[key: string]: PlainPrim}
export type Plain = PlainPrim | PlainObject | Plain[] | [Plain, Plain] | DateTime
type PlainPrim = string | number | boolean | null | undefined

export type JsonObject = {[key: string]: Json}
export type Json = JsonPrim | JsonObject | Json[] | [Json, Json]
export type JsonPrim = string | number | boolean | null
export type NotNull<T> = Exclude<Exclude<T, null>, undefined>
export type Prettify<T> = {[K in keyof T]: T[K]} & {}
export type Maybe<T> = T | null | undefined
export type NotNullProps<T> = {
  [K in keyof T]: NonNullable<T[K]>
}

export const notNull = <TValue>(v: TValue | null | undefined): v is TValue =>
  v === null || v === undefined ? false : true

// Define a custom Zod schema for Luxon DateTime validation
const zLuxonDateTime = z.custom<DateTime<true>>(value => DateTime.isDateTime(value) && value.isValid)

/**
 * zod extensions
 */
export namespace zx {
  export namespace nullish {
    /*** coerces as best as it can to `number | undefined` */
    export const num = z.preprocess(
      value =>
        match(value)
          .with(P.string, s => Number.parseFloat(s))
          .with(P.nullish, () => undefined)
          .otherwise(() => value),
      z.number().optional()
    )

    /*** coerces as best as it can to `string | undefined` */
    export const string = z.preprocess(
      value =>
        match(value)
          .with(P.nullish, () => undefined)
          .with(P.string, s => s)
          .otherwise(s => s?.toString()),
      z.string().optional()
    )

    export const bool = z.preprocess(
      value =>
        match(value)
          .with(P.nullish, '', () => undefined)
          .with('true', '1', _ => true)
          .with('false', '0', _ => false)
          .otherwise(s => Boolean(s)),
      z.boolean().optional()
    )

    export const date = z.preprocess(
      value =>
        match(value)
          .with(P.nullish, () => undefined)
          .with(P.string, d => DateTime.fromISO(d))
          .with(
            P.when(d => DateTime.isDateTime(d)),
            d => d
          )
          .otherwise(() => undefined),
      zLuxonDateTime.optional()
    )
  }

  /** coerces from string to a luxon DateTime */
  export const date = z.preprocess(
    value =>
      match(value)
        .with(P.string, d => DateTime.fromISO(d))
        .with(
          P.when(d => DateTime.isDateTime(d)),
          d => d
        )
        .otherwise(() => undefined),
    zLuxonDateTime
  )

  export const enumFromObjKeys = <K extends string>(obj: Record<K, any>) => {
    const [firstKey, ...otherKeys] = Object.keys(obj) as K[]
    return z.enum([firstKey!, ...otherKeys])
  }

  export const enumPassthrough = <T extends [string, ...string[]]>(e: z.ZodEnum<T>) => z.union([e, z.string()])

  /**
   * like z.enum but returns undefined if the value is not in the enum
   */
  export const enumSafe = <T extends [string, ...string[]]>(e: z.ZodEnum<T>) =>
    z.preprocess(
      value =>
        match(e.safeParse(value))
          .with({success: true}, p => p.data)
          .otherwise(() => undefined),
      e.optional()
    )

  export const pipeArray = <R = string>(mapFn?: (s: string) => R) =>
    z
      .string()
      .optional()
      .transform(s => {
        if (!s) return undefined
        const parts = s.split('|')
        return mapFn ? parts.map(mapFn) : (parts as R[])
      })
  export const nullishToUndefined = <I extends ZodTypeAny>(schema: I) =>
    z.preprocess(value => (value === null ? undefined : value), schema)

  export const fmtError = (error: z.ZodError): string => {
    const formattedError = error.format()
    const processErrors = (obj: any, prefix = ''): string[] =>
      entries(obj)
        .filter(([key]) => key !== '_errors')
        .flatMap(([field, value]) => {
          const fullField = prefix ? `${prefix}.${field}` : field
          const messages = (value as any)?._errors
          const nestedErrors = typeof value === 'object' && value !== null ? processErrors(value, fullField) : []
          if (!messages?.length) return nestedErrors
          return [
            messages.length === 1 ?
              `'${fullField}': ${messages[0]}`
            : `'${fullField}':\n${messages.map((msg: string) => `- ${msg}`).join('\n')}`,
            ...nestedErrors
          ]
        })

    const fieldErrors = processErrors(formattedError).join('\n')
    const generalErrors =
      formattedError._errors?.length ?
        formattedError._errors.length === 1 ?
          formattedError._errors[0]
        : `General Errors:\n${formattedError._errors.map(msg => `- ${msg}`).join('\n')}`
      : ''
    const errors = [fieldErrors, generalErrors].filter(Boolean)
    return errors.length ? errors.join('\n') : ''
  }
}

/** same as safeParse but returns out ok/error types. We wrap the ok result in data because with parsing we often want to exclude the ok after */
export const parseOrErr = <T extends ZodSchema, J>(
  zObj: T,
  j: J
): OK<{data: z.infer<T>}> | Err<{message: string; data: J}> => {
  const res = zObj.safeParse(j)
  if (res.success) return ok({data: res.data as z.infer<T>})
  return err({message: zx.fmtError(res.error), data: j})
}

export const parseObjOrErr = <T extends ZodObject<any>, J>(
  zObj: T,
  j: J
): OK<z.infer<T>> | Err<{message: string; data: J}> => {
  const res = zObj.safeParse(j)
  if (res.success) return ok(res.data as z.infer<T>)
  return err({message: zx.fmtError(res.error), data: j})
}

export const parseOrDefault = <T extends ZodSchema, D = undefined>(
  zObj: T,
  j?: unknown,
  d?: D,
  onError?: (e: z.SafeParseError<z.infer<T>>, d: unknown) => void
): z.infer<T> | D => {
  if (!j) return d
  const result = zObj.safeParse(j)
  if (result.success) return result.data
  onError?.(result, d)
  return d
}

/**
 * Parses a list of items. Retursn an error with a summary of parising errors, or the fully parsed list
 */
export const parseList = <T extends ZodRawShape>(
  zObj: ZodObject<T>,
  list: unknown[]
): OK<z.infer<ZodObject<T>>[]> | Err => {
  const [oks, errs] = parseOrErrPartition(zObj, list)
  if (errs.length > 0)
    return err(
      `error ${errs.length} items in list: ${take(errs, 3)
        .map(e => e.message)
        .join('\n')}`
    )
  return ok(oks)
}

export const safeParseList = <T extends ZodRawShape>(zObj: ZodObject<T>, list: unknown[]): z.infer<ZodObject<T>>[] =>
  list
    .map(i => parseOrDefault(zObj, i, undefined, e => console.error('error parsing item', e.error.message, i)))
    .filter(notNull) as z.infer<ZodObject<T>>[]

/**
 * Partitions a list of items into successful and failed parsed results.
 *
 * @param zObj - The Zod schema to validate against
 * @param list - Array of items to parse
 * @returns A tuple where the first element contains successfully parsed data (without ok wrapper) and the second contains error objects
 *
 * @example
 * ```ts
 * const schema = z.object({ name: z.string(), age: z.number() })
 * const data = [{ name: 'John', age: 30 }, { name: 'Jane' }, { name: 'Bob', age: 'invalid' }]
 * const [successful, failed] = parseOrErrPartition(schema, data)
 * // successful: [{ name: 'John', age: 30 }]
 * // failed: [Err<{message: string}>] // contains validation errors for Jane and Bob
 * ```
 */
export const parseOrErrPartition = <T extends ZodRawShape>(zObj: ZodObject<T>, list: unknown[]) => {
  const results = list.map(l => parseOrErr(zObj, l))
  const [successful, failed] = partition(results, i => i.ok)
  return [successful.map(s => s.data), failed] as [z.infer<ZodObject<T>>[], Err<{message: string}>[]]
}

export const replaceUndefinedWithNull = (value: any): any => {
  if (value === undefined) return null
  if (value === null || typeof value !== 'object') return value
  if (Array.isArray(value)) return value.map(replaceUndefinedWithNull) as any
  return mapValues(value, v => replaceUndefinedWithNull(v))
}

/**
 * Helper function to create a type that omits properties from a discriminated union
 * while preserving the union structure
 */
export type UnionOmit<TUnion extends {[K in D]: string}, D extends string, K extends keyof TUnion | (keyof TUnion)[]> =
  TUnion extends any ?
    K extends keyof TUnion ? Omit<TUnion, K>
    : K extends (keyof TUnion)[] ? Omit<TUnion, K[number]>
    : never
  : never

// Helper to get the union of all keys from a union of objects T
type AllKeys<T> = T extends unknown ? keyof T : never
// Helper to get the combined type for a specific key K across the union T
// If K is not present in a member, it contributes `undefined` to the union
type ValueForKey<TUnion, K> =
  TUnion extends unknown ?
    K extends keyof TUnion ?
      TUnion[K]
    : undefined
  : never

/**
 * type helper to get all the properties of a union type T,
 * combining their types. If a property is not present in all members
 * of the union (i.e., not in the intersection), its resulting type
 * will include `undefined`.
 */
export type UnionAny<T> = {
  // Map over the union of all keys from all members of T
  [K in AllKeys<T>]: ValueForKey<T, K> // Calculate the combined type for each key K
}
